import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useAdminStudent, useEnrollmentAccess } from "@/hooks/useAdminData";
import type { StudentEnrollment } from "@/lib/api-types";
import { Calendar, Mail, Shield, User } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

export function AdminStudentDetail() {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const mutation = useEnrollmentAccess(id ?? "");
	const openingButton = useRef<HTMLButtonElement | null>(null);
	const enrollmentHeading = useRef<HTMLHeadingElement | null>(null);
	const [target, setTarget] = useState<{
		studentId: string;
		action: "disable" | "enable";
		enrollment: StudentEnrollment | "all";
	} | null>(null);
	const { data: student, isLoading } = useAdminStudent(id ?? "");

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-6 w-64" />
				<Skeleton className="h-8 w-48" />
				<Skeleton className="h-48 rounded-xl" />
				<Skeleton className="h-64 rounded-xl" />
			</div>
		);
	}

	if (!student) {
		return <div className="py-12 text-center text-secondary">{t("admin.studentNotFound")}</div>;
	}

	const { user } = student;
	const selectedEnrollment = target?.studentId === user.id ? target.enrollment : null;
	const hasActiveEnrollments = student.enrollments.some((e) => e.status === "active");
	const enabling = target?.action === "enable";
	function confirmAccess() {
		if (!selectedEnrollment || target?.studentId !== id || mutation.isPending) return;
		mutation.mutate(
			enabling && selectedEnrollment !== "all"
				? { action: "enable", body: { course_id: selectedEnrollment.course_id } }
				: {
						action: "disable",
						body:
							selectedEnrollment === "all"
								? { all: true }
								: { course_id: selectedEnrollment.course_id },
					},
			{
				onSuccess: () => {
					setTarget(null);
					toast.success(t(enabling ? "admin.accessEnabled" : "admin.accessDisabled"));
				},
				onError: () =>
					toast.error(t(enabling ? "admin.enableAccessFailed" : "admin.disableAccessFailed")),
			},
		);
	}

	return (
		<div className="space-y-6">
			<AdminBreadcrumb
				items={[
					{ label: t("admin.dashboard"), href: "/admin" },
					{ label: t("admin.students"), href: "/admin/students" },
					{ label: user.name },
				]}
			/>

			<h1 className="text-3xl font-bold text-primary">{user.name}</h1>

			{/* Student info card */}
			<div className="rounded-xl border border-white/10 bg-bg-elevated p-6">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
					<div className="flex items-center gap-3">
						<div className="rounded-lg bg-accent-muted p-2">
							<User className="h-4 w-4 text-accent" />
						</div>
						<div>
							<p className="text-xs text-muted">{t("admin.name")}</p>
							<p className="font-medium text-primary">{user.name}</p>
						</div>
					</div>
					<div className="flex items-center gap-3">
						<div className="rounded-lg bg-accent-muted p-2">
							<Mail className="h-4 w-4 text-accent" />
						</div>
						<div>
							<p className="text-xs text-muted">{t("admin.email")}</p>
							<p className="font-medium text-primary">{user.email}</p>
						</div>
					</div>
					<div className="flex items-center gap-3">
						<div className="rounded-lg bg-accent-muted p-2">
							<Calendar className="h-4 w-4 text-accent" />
						</div>
						<div>
							<p className="text-xs text-muted">{t("admin.lastActive")}</p>
							<p className="font-medium text-primary">
								{user.updated_at ? new Date(user.updated_at).toLocaleDateString() : "-"}
							</p>
						</div>
					</div>
					<div className="flex items-center gap-3">
						<div className="rounded-lg bg-accent-muted p-2">
							<Shield className="h-4 w-4 text-accent" />
						</div>
						<div>
							<p className="text-xs text-muted">{t("admin.quizAttempts")}</p>
							<p className="font-medium text-primary">{student.quiz_history?.length ?? 0}</p>
						</div>
					</div>
				</div>
			</div>

			{/* Enrollments */}
			<div className="rounded-xl border border-white/10 bg-bg-elevated p-6">
				<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
					<h2 ref={enrollmentHeading} tabIndex={-1} className="text-lg font-semibold text-primary">
						{t("admin.enrollments")}
					</h2>
					<Button
						variant="destructive"
						disabled={!hasActiveEnrollments || mutation.isPending}
						onClick={(event) => {
							openingButton.current = event.currentTarget;
							setTarget({ studentId: user.id, action: "disable", enrollment: "all" });
						}}
					>
						{t("admin.disableAllAccess")}
					</Button>
				</div>
				{student.enrollments && student.enrollments.length > 0 ? (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("admin.courseTitle")}</TableHead>
								<TableHead>{t("admin.enrolledDate")}</TableHead>
								<TableHead>{t("admin.status")}</TableHead>
								<TableHead>{t("admin.actions")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{student.enrollments.map((enrollment) => (
								<TableRow key={enrollment.id}>
									<TableCell className="font-medium text-primary">
										{enrollment.course_title}
									</TableCell>
									<TableCell className="text-secondary">
										{new Date(enrollment.enrolled_at).toLocaleDateString()}
									</TableCell>
									<TableCell>
										<Badge
											className={
												enrollment.status === "active"
													? "border-transparent bg-success-muted text-success"
													: "border-transparent bg-bg-overlay text-secondary"
											}
										>
											{enrollment.status === "disabled" ? t("admin.disabled") : enrollment.status}
										</Badge>
									</TableCell>
									<TableCell>
										{(enrollment.status === "active" || enrollment.status === "disabled") && (
											<Button
												key={enrollment.status}
												variant="outline"
												size="sm"
												disabled={mutation.isPending}
												onClick={(event) => {
													openingButton.current = event.currentTarget;
													setTarget({
														studentId: user.id,
														action: enrollment.status === "disabled" ? "enable" : "disable",
														enrollment,
													});
												}}
												aria-label={t(
													enrollment.status === "disabled"
														? "admin.enableCourseAccess"
														: "admin.disableCourseAccess",
													{
														course: enrollment.course_title,
													},
												)}
											>
												{t(
													enrollment.status === "disabled"
														? "admin.enableAccess"
														: "admin.disableAccess",
												)}
											</Button>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				) : (
					<p className="py-8 text-center text-secondary">{t("admin.noEnrollments")}</p>
				)}
			</div>
			<Dialog
				open={selectedEnrollment !== null}
				onOpenChange={(open) => {
					if (!open && !mutation.isPending) setTarget(null);
				}}
			>
				<DialogContent
					className="motion-reduce:animate-none"
					onCloseAutoFocus={(event) => {
						event.preventDefault();
						const button = openingButton.current;
						if (button?.isConnected && !button.disabled) button.focus();
						else enrollmentHeading.current?.focus();
					}}
				>
					<DialogHeader>
						<DialogTitle>{t(enabling ? "admin.enableAccess" : "admin.disableAccess")}</DialogTitle>
						<DialogDescription>
							{selectedEnrollment === "all"
								? t("admin.disableAllAccessConfirm", { student: user.email })
								: t(
										enabling
											? "admin.enableCourseAccessConfirm"
											: "admin.disableCourseAccessConfirm",
										{
											student: user.email,
											course: selectedEnrollment?.course_title,
										},
									)}
						</DialogDescription>
					</DialogHeader>
					<p className="text-sm text-secondary">
						{t(enabling ? "admin.enableAccessHistory" : "admin.disableAccessHistory")}
					</p>
					<DialogFooter>
						<Button variant="outline" disabled={mutation.isPending} onClick={() => setTarget(null)}>
							{t("admin.cancel")}
						</Button>
						<Button
							variant={enabling ? "default" : "destructive"}
							disabled={mutation.isPending}
							onClick={confirmAccess}
						>
							{t(
								enabling
									? mutation.isPending
										? "admin.enablingAccess"
										: "admin.confirmEnableAccess"
									: mutation.isPending
										? "admin.disablingAccess"
										: "admin.confirmDisableAccess",
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
